import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, type TaskInstance } from 'ember-concurrency';
import type MarketParityParityManagerController from 'lumen/controllers/market/parity/parity-manager';
import type {
  ParityManagerFiltersMarketInfo,
  ParityManagerStat,
} from 'lumen/models';
import {
  ParityManagerEscalationinfo,
  ParityManagerFiltersAssignee,
  ParityManagerFiltersChannel,
  ParityManagerFiltersEntryMode,
  ParityManagerFiltersEscalationLevel,
  ParityManagerFiltersMarketplace,
  ParityManagerFiltersRootCause,
  ParityManagerFiltersStatus,
  ParityManagerIssuestat,
  ParityManagerIssuesummary,
} from 'lumen/models';
import { ISSUE_TYPES } from 'lumen/models/parity-manager/issue';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type ParityManagerQueryParams = {
  assignees: string;
  channels: string;
  entryModes: string;
  escalationLevels: string;
  fromDate: string;
  rootcauses: string;
  searchValue: string;
  selectedIssueSummaryId: string;
  selectedMetric: string;
  statuses: string;
  toDate: string;
};

export interface Filters {
  assignees: ParityManagerFiltersAssignee[];
  channels: ParityManagerFiltersChannel[];
  entryModes: ParityManagerFiltersEntryMode[];
  escalationLevels: ParityManagerFiltersEscalationLevel[];
  marketplaces: ParityManagerFiltersMarketplace[];
  rootcauses: ParityManagerFiltersRootCause[];
  statuses: ParityManagerFiltersStatus[];
}

interface Stats {
  escalations: ParityManagerEscalationinfo[];
  stats: ParityManagerIssuestat[];
  summaries: ParityManagerIssuesummary[];
}

type ParityManagerModel = {
  data: Partial<TaskInstance<Stats>>;
  filters: Partial<TaskInstance<Filters>>;
};

export default class MarketParityParityManagerRoute extends Route {
  queryParams = {
    selectedMetric: {
      refreshModel: true,
    },
    selectedIssueSummaryId: {
      refreshModel: false,
    },
    channels: {
      refreshModel: true,
    },
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    searchValue: {
      refreshModel: true,
    },
    assignees: {
      refreshModel: true,
    },
    statuses: {
      refreshModel: true,
    },
    rootcauses: {
      refreshModel: true,
    },
    escalationLevels: {
      refreshModel: true,
    },
    entryModes: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  // Cache the filter values so we don't get flashing values in the dropdowns when a user changes a filter
  @tracked cachedFilterValues: Filters | null = null;
  @tracked cachedStats: ParityManagerIssuestat[] | null = null;
  @tracked lastActiveMarketId = '';

  modelObject: ParityManagerModel = trackedObject({
    data: {
      isError: false,
      isRunning: false,
      value: {
        escalations: [] as ParityManagerEscalationinfo[],
        stats: this.cachedStats ?? [],
        summaries: [] as ParityManagerIssuesummary[],
      },
    },
    filters: {
      isError: false,
      isRunning: false,
      value: {
        assignees: this.cachedFilterValues?.assignees ?? [],
        channels: this.cachedFilterValues?.channels ?? [],
        entryModes: this.cachedFilterValues?.entryModes ?? [],
        escalationLevels: this.cachedFilterValues?.escalationLevels ?? [],
        marketplaces: this.cachedFilterValues?.marketplaces ?? [],
        rootcauses: this.cachedFilterValues?.rootcauses ?? [],
        statuses: this.cachedFilterValues?.statuses ?? [],
      },
    },
  });

  fetchParityManagerDataTask = task(
    { restartable: true },
    async ({ params }) => {
      try {
        const localToday = this.hotelAndMarketService.getLocalTime();
        const statsParams = {
          namespace: APIGATEWAY,
          typeKey: 'paritymanager/stats',
          market_id: this.hotelAndMarketService.market.id,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          issue_type: params.selectedMetric,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          search_query: params.searchValue,
          // Default fromDate is 6 days ago, default toDate is today (default range is last week)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          extract_date_start:
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            params.fromDate ??
            localToday.subtract(6, 'day').format('YYYY-MM-DD'),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          extract_date_end: params.toDate ?? localToday.format('YYYY-MM-DD'),
          // Filters
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          ota_id: params.channels,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          assignee: params.assignees,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          status: params.statuses,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          root_cause: params.rootcauses,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          all_channels: params.channels,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          escalation_level: params.escalationLevels,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          entry_mode: params.entryModes,
        };

        const statsData = await this.store.query<ParityManagerStat>(
          'parity-manager/stat',
          statsParams,
        );
        const issueStats = statsData.filter(
          (stat) => stat instanceof ParityManagerIssuestat,
        );

        // @ts-expect-error: Incorrect type
        this.cachedStats = issueStats;

        return {
          stats: issueStats,
          summaries: statsData.filter(
            (stat) => stat instanceof ParityManagerIssuesummary,
          ),
          escalations: statsData.filter(
            (stat) => stat instanceof ParityManagerEscalationinfo,
          ),
        };
      } catch (error) {
        this.errorTracking.captureException(error);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );
  fetchParityManagerMarketInfoTask = task({ restartable: true }, async () => {
    try {
      const filterParams = {
        namespace: APIGATEWAY,
        typeKey: 'usermanagement/parity_manager_market_info',
        market_id: this.hotelAndMarketService.market.id,
      };
      const marketInfo = await this.store.query<ParityManagerFiltersMarketInfo>(
        'parity-manager/filters/market-info',
        filterParams,
      );

      const channels = marketInfo
        .filter((filter) => filter instanceof ParityManagerFiltersChannel)
        .sort((a, b) => a.name.localeCompare(b.name));
      const assignees = marketInfo
        .filter((filter) => filter instanceof ParityManagerFiltersAssignee)
        .sort((a, b) => a.name.localeCompare(b.name));
      const statuses = marketInfo
        .filter((filter) => filter instanceof ParityManagerFiltersStatus)
        .sort((a, b) => a.name.localeCompare(b.name));
      const rootcauses = marketInfo
        .filter((filter) => filter instanceof ParityManagerFiltersRootCause)
        .sort((a, b) => a.name.localeCompare(b.name));
      const escalationLevels = marketInfo.filter(
        (filter) => filter instanceof ParityManagerFiltersEscalationLevel,
      );
      const entryModes = marketInfo.filter(
        (filter) => filter instanceof ParityManagerFiltersEntryMode,
      );
      const marketplaces = marketInfo.filter(
        (filter) => filter instanceof ParityManagerFiltersMarketplace,
      );

      const filters = {
        channels,
        assignees,
        statuses,
        rootcauses,
        escalationLevels,
        entryModes,
        marketplaces,
      };

      // @ts-expect-error: Incorrect type
      this.cachedFilterValues = filters;

      return filters;
    } catch (error) {
      this.errorTracking.captureException(error);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  beforeModel(transition: Transition): void {
    if (!this.access.hasParityManagerAccess) {
      this.transition.redirectToEnabledTab(transition);
    } else if (
      this.lastActiveMarketId !== this.hotelAndMarketService.market.id &&
      transition.from
    ) {
      // Clear the QPs when switching markets but not on refresh!
      const { fromDate, toDate } = transition.to.queryParams;

      // Clear the parameters (except fromDate and toDate) if you switch markets (e.g. filters are market specific and can cause ugly API errors)
      transition.abort();
      this.router.transitionTo(
        'market.parity.parity-manager',
        this.hotelAndMarketService.market.id,
        { queryParams: { fromDate, toDate } },
      );
    } else {
      this.transition.validateParityManagerQueryParams(
        transition,
        this.paramsFor(this.routeName), // Use paramsFor to obtain the deserialised query params
      );
    }

    // @ts-expect-error: Type 'string | null' is not assignable to type 'string'.
    this.lastActiveMarketId = this.hotelAndMarketService.market.id;
  }

  deactivate(): void {
    // Clear stored data
    this.cachedFilterValues = null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/require-await
  async model(params: ParityManagerQueryParams) {
    if (!this.cachedFilterValues) {
      // @ts-expect-error: Incorrect type
      this.modelObject.filters =
        this.fetchParityManagerMarketInfoTask.perform();
    }

    // @ts-expect-error: Incorrect type
    this.modelObject.data = this.fetchParityManagerDataTask.perform({ params });

    return this.modelObject;
  }

  // @ts-expect-error: Property 'resetController' in type 'ParityManagerRoute' is not assignable to the same property in base type 'Route'.

  resetController(
    controller: MarketParityParityManagerController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      // Clear data when the route is exiting
      controller.assignees = [];
      controller.channels = [];
      controller.statuses = [];
      controller.rootcauses = [];
      controller.escalationLevels = [];
      controller.entryModes = [];
      controller.selectedMetric = ISSUE_TYPES.MAJOR_OTA;
      controller.searchValue = '';

      this.store.unloadAll('parity-manager/issuestat');
      this.store.unloadAll('parity-manager/issuesummary');
      this.store.unloadAll('parity-manager/escalationinfo');
      this.store.unloadAll('parity-manager/issue');
    }
  }
}
